import React from 'react';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { ClientSalesReportsModal } from '@/types/modal';
import { ErrorModal, LoadingModal, NoFooterModal } from '@/components/molecules/common/Modal';
import Table from '@/components/molecules/common/Table';
import { useClientSalesReports } from '@/hooks/client';
import { useErrorHandling } from '@/hooks/common';

const getFileName = (filePath?: string | null) => filePath?.split('/').pop() || '-';

const View: React.FC<{ modal: ClientSalesReportsModal }> = ({ modal }) => {
  const { t } = useTranslation();

  const { client, onClose } = modal;
  const { data: reports = [], isLoading, isError, error } = useClientSalesReports(client.clientId);
  const { getErrorMsgFromResponse } = useErrorHandling();

  if (isLoading) {
    return (
      <LoadingModal
        title={t('modal.client_sales_reports.title')}
        onClose={onClose}
      />
    );
  }

  if (isError) {
    return (
      <ErrorModal
        title={t('modal.client_sales_reports.title')}
        message={getErrorMsgFromResponse(error)}
        onClose={onClose}
      />
    );
  }

  const columns: GridColDef[] = [
    { field: 'year', headerName: t('modal.client_sales_reports.year'), width: 160 },
    {
      field: 'fileName',
      headerName: t('modal.client_sales_reports.file_name'),
      flex: 1,
      renderCell: (params: GridCellParams) =>
        params.row.presignedUrl ? (
          <Link
            href={params.row.presignedUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {params.row.fileName}
            <OpenInNewIcon sx={{ fontSize: '1em' }} />
          </Link>
        ) : (
          params.row.fileName
        ),
    },
  ];

  const rows = [...reports]
    .sort((a, b) => b.year - a.year)
    .map((report) => ({
      id: report.year,
      year: report.year,
      fileName: getFileName(report.file_path),
      presignedUrl: report.presigned_url,
    }));

  const body = (
    <div>
      {rows.length ? (
        <Table
          rows={rows}
          columns={columns}
          loading={false}
          table={{
            kind: 'Table',
          }}
        />
      ) : (
        <p>{t('modal.client_sales_reports.no_reports')}</p>
      )}
    </div>
  );

  return (
    <NoFooterModal
      title={t('modal.client_sales_reports.title')}
      onClose={onClose}
      body={body}
      modalName="client_sales_reports"
      containerStyle={{ width: 640, maxWidth: '90vw' }}
    />
  );
};

export default View;
